import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFinance } from '@/contexts/FinanceContext';
import type { FinanceTransaction } from '@/types/finance';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  transaction: FinanceTransaction | null;
}

const fmtBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

const parseBrl = (input: string): number => {
  const trimmed = input.trim();
  if (!trimmed) return 0;
  const parsed = parseFloat(trimmed.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

function todayInputValue(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

function parseDateInput(value: string): Date {
  if (!value) return new Date();
  const [y, m, d] = value.split('-').map((s) => Number.parseInt(s, 10));
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

export function MarkAsPaidDialog({ open, onOpenChange, transaction }: Props) {
  const { markAsCompleted } = useFinance();
  const [paymentDate, setPaymentDate] = useState(todayInputValue());
  const [fine, setFine] = useState('');
  const [interest, setInterest] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open && transaction) {
      // Default: data de hoje (não a do vencimento — é raro pagar exatamente no vencimento
      // antigo se já está pendente). Multa e juros começam vazios.
      setPaymentDate(todayInputValue());
      setFine(transaction.fine ? transaction.fine.toFixed(2).replace('.', ',') : '');
      setInterest(transaction.interest ? transaction.interest.toFixed(2).replace('.', ',') : '');
    }
  }, [open, transaction]);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (!transaction) return null;

  const fineNum = parseBrl(fine);
  const interestNum = parseBrl(interest);
  const total = transaction.amount + fineNum + interestNum;
  const hasExtras = fineNum > 0 || interestNum > 0;
  const isIncome = transaction.type === 'income';

  const handleConfirm = () => {
    setSubmitting(true);
    try {
      markAsCompleted(transaction.id, {
        paymentDate: parseDateInput(paymentDate),
        ...(fineNum > 0 || transaction.fine ? { fine: fineNum } : {}),
        ...(interestNum > 0 || transaction.interest ? { interest: interestNum } : {}),
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-brand">
            <CheckCircle2 className="h-5 w-5 text-income" />
            Confirmar {isIncome ? 'recebimento' : 'pagamento'}
          </DialogTitle>
          <DialogDescription className="line-clamp-2">{transaction.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Valor original</span>
              <span className="font-medium tabular-nums">{fmtBrl(transaction.amount)}</span>
            </div>
            {hasExtras && (
              <>
                {fineNum > 0 && (
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Multa</span>
                    <span className="tabular-nums text-expense">+ {fmtBrl(fineNum)}</span>
                  </div>
                )}
                {interestNum > 0 && (
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Juros</span>
                    <span className="tabular-nums text-expense">+ {fmtBrl(interestNum)}</span>
                  </div>
                )}
              </>
            )}
            <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
              <span className="font-semibold">{isIncome ? 'Total a receber' : 'Total a pagar'}</span>
              <span
                className={`text-lg font-bold tabular-nums ${isIncome ? 'text-income' : 'text-expense'}`}
              >
                {fmtBrl(total)}
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mp-date">Data {isIncome ? 'do recebimento' : 'do pagamento'}</Label>
            <Input
              id="mp-date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="mp-fine">Multa (R$)</Label>
              <Input
                id="mp-fine"
                placeholder="0,00"
                value={fine}
                onChange={(e) => setFine(e.target.value)}
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mp-interest">Juros (R$)</Label>
              <Input
                id="mp-interest"
                placeholder="0,00"
                value={interest}
                onChange={(e) => setInterest(e.target.value)}
                inputMode="decimal"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            variant={isIncome ? 'income' : 'default'}
            className={
              isIncome ? '' : 'bg-accent font-semibold text-accent-foreground hover:bg-accent/90'
            }
          >
            Confirmar {isIncome ? 'recebimento' : 'pagamento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
